import { useEffect, useMemo, useState } from "react";
import {
  fetchMailBody,
  fetchMailFolders,
  fetchMailMessages,
  fetchMailSent,
  fetchMailSentOne,
  queueMailReply,
  searchMail,
} from "@/lib/ccApi";
import { cn } from "@/lib/utils";
import { MonoMeta } from "@/design/primitives";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import registry from "@/data/mailAccounts.json";

// Airmail-style mail client (Jeff 2026-10-21: "it should be the airmail looking type
// of style"). Same mental model Jeff approved 10-20 evening — brands → accounts →
// to-process vs completed — restyled as a desktop mail client: cool-gray sidebar,
// dense three-line message list with unread dots, full-height reading pane.

const MAILBOX_KIND = "mail-mailbox";
const ADDRESS_KIND = "mail-address";
const MSG_KIND = "mail-msg";

// The account list is NOT hard-coded here any more — it comes from the baked
// registry (frontend/src/data/mailAccounts.json), which the engine writes from
// SYSTEM/kit-os/mirror/mail-brand-accounts.json. Adding a mail account is now a
// registry edit, not a code edit (spec Sheet F).
const REGISTRY_ACCOUNTS = (registry.accounts || []).filter((a) => a.enabled);
const ACCOUNTS_BY_BRAND = REGISTRY_ACCOUNTS.reduce((acc, a) => {
  (acc[a.brand] = acc[a.brand] || []).push(a.address);
  return acc;
}, {});
const BRAND_TITLE = {
  ...(registry.brands || {}),
  __unfiled: "Not sorted yet",
};
// A brand with no enabled account does not render a row.
const BRAND_LIST = Object.keys(ACCOUNTS_BY_BRAND).sort(
  (a, b) => (BRAND_TITLE[a] || a).localeCompare(BRAND_TITLE[b] || b),
);
const ACCOUNT_RECORD = REGISTRY_ACCOUNTS.reduce((acc, a) => {
  acc[a.address] = a;
  return acc;
}, {});

function fmtWhen(sentAt, ageDays) {
  if (!sentAt) return "";
  const d = new Date(sentAt);
  if (Number.isNaN(d.getTime())) return "";
  if (ageDays != null && ageDays <= 1) {
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  if (ageDays != null && ageDays < 7) {
    return d.toLocaleDateString("en-US", { weekday: "short" });
  }
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

// ── message row — Airmail density: sender ↑, subject, snippet; unread dot ──
function MsgRow({ msg, selected, onSelect }) {
  const p = msg.payload || {};
  const flags = p.flags || [];
  const unread = msg.state !== "answered" && p.mailbox === "needs_reply";
  const alarm = flags.includes("waiting-old") || flags.includes("money");
  return (
    <button
      type="button"
      data-testid="mail-msg-row"
      data-mailbox={p.mailbox}
      onClick={() => onSelect(msg)}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "w-full border-b border-[#E7E7EA] px-3 py-[7px] text-left",
        "grid grid-cols-[14px_1fr_auto] items-start gap-x-2",
        selected ? "bg-os-ink text-white" : "bg-white hover:bg-[#F2F2F4]",
      )}
    >
      {/* unread / alarm dot */}
      <span className="relative mt-[5px] flex justify-center">
        {alarm ? (
          <span className="h-[8px] w-[8px] rounded-full bg-os-red" />
        ) : unread ? (
          <span className={cn("h-[7px] w-[7px] rounded-full", selected ? "bg-white" : "bg-os-gold")} />
        ) : null}
      </span>

      <span className="min-w-0">
        {/* line 1 — sender + brand tag */}
        <span className="flex items-baseline gap-1.5">
          <span className={cn("truncate text-[12.5px]", unread && !selected ? "font-bold text-os-ink" : selected ? "font-semibold" : "font-medium text-os-ink-2")}>
            {p.from}
          </span>
          <span className={cn(
            "shrink-0 font-mono text-[8.5px] uppercase tracking-[0.08em]",
            selected ? "text-white/60" : "text-os-ink-4",
          )}>
            {p.brandLabel}
          </span>
        </span>
        {/* line 2 — subject */}
        <span className={cn("mt-px block truncate text-[12.5px]", unread && !selected ? "font-semibold text-os-ink" : selected ? "text-white/95" : "text-os-ink-2")}>
          {p.subject}
        </span>
        {/* line 3 — snippet */}
        <span className={cn("mt-px block truncate text-[11.5px] leading-snug", selected ? "text-white/60" : "text-os-ink-3")}>
          {p.preview}
        </span>
        {/* status tags — only when they matter */}
        {(msg.state === "answered" || msg.state === "bounced" || flags.includes("money") || flags.includes("waiting-old") || p.tier) && (
          <span className="mt-0.5 flex gap-1.5">
            {msg.state === "answered" && <span className={cn("text-[9.5px] font-semibold", selected ? "text-white/70" : "text-os-green")}>✓ handled</span>}
            {msg.state === "bounced" && <span className={cn("text-[9.5px]", selected ? "text-white/50" : "text-os-ink-4")}>not delivered</span>}
            {flags.includes("money") && <span className={cn("text-[9.5px] font-semibold", selected ? "text-white" : "text-os-red")}>money</span>}
            {flags.includes("waiting-old") && <span className={cn("text-[9.5px] font-semibold", selected ? "text-white" : "text-os-red")}>waiting — old</span>}
            {p.tier === "txn" && <span className={cn("text-[9.5px]", selected ? "text-white/60" : "text-os-ink-4")}>receipt</span>}
            {p.tier === "noise" && <span className={cn("text-[9.5px]", selected ? "text-white/50" : "text-os-ink-4")}>noise</span>}
            {!p.tier && msg.state !== "answered" && msg.state !== "bounced" && (!selected) && <span className="text-[9.5px] text-os-ink-4">unfiled</span>}
          </span>
        )}
      </span>

      <span className={cn("whitespace-nowrap pt-px text-right font-mono text-[10px] leading-relaxed", selected ? "text-white/70" : "text-os-ink-3")}>
        {fmtWhen(p.sentAt, p.ageDays)}
        {flags.includes("getting-old") && (
          <span className={cn("mt-0.5 block text-[9.5px] font-semibold", selected ? "text-white/80" : "text-os-amber")}>getting old</span>
        )}
      </span>
    </button>
  );
}

// ── reading pane ─────────────────────────────────────────────
function Reader({ msg, board, onNotice, variant }) {
  const p = msg?.payload || {};
  const [showNote, setShowNote] = useState(false);
  const [noteVal, setNoteVal] = useState("");
  const [fullBody, setFullBody] = useState(null);
  // The HTML leg of the body (backend answers null for plain-text-only mail).
  const [fullBodyHtml, setFullBodyHtml] = useState(null);
  const [bodyState, setBodyState] = useState("idle");
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyState, setReplyState] = useState("idle");
  const [replyMsg, setReplyMsg] = useState("");

  const bodyKey = useMemo(() => {
    if (!msg) return null;
    const parts = (msg.slug || "").split(":");
    return parts.length >= 4 ? parts[parts.length - 1] : null;
  }, [msg]);

  useEffect(() => {
    setFullBody(null); setFullBodyHtml(null); setBodyState("idle"); setReplyOpen(false);
    setReplyText(""); setReplyState("idle"); setReplyMsg("");
    if (!bodyKey) return;
    let dead = false;
    setBodyState("loading");
    fetchMailBody(bodyKey)
      .then((d) => { if (!dead) { setFullBody(d.body); setFullBodyHtml(d.bodyHtml || null); setBodyState("ready"); } })
      .catch(() => { if (!dead) setBodyState("missing"); });
    return () => { dead = true; };
  }, [bodyKey]);

  // Poll the body endpoint while the message is selected and its full text is
  // still missing (Airmail parity, Jeff 2026-11-03): the engine's next push
  // (≤2 min) closes the gap WITHOUT a page reload or a re-click. Visibility-
  // gated like the folder leg, so it costs nothing in a background tab.
  useEffect(() => {
    if (bodyState !== "missing" || !bodyKey) return;
    let dead = false;
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      fetchMailBody(bodyKey)
        .then((d) => {
          if (!dead && d.body) { setFullBody(d.body); setFullBodyHtml(d.bodyHtml || null); setBodyState("ready"); }
        })
        .catch(() => { /* still missing — stay quiet, retry next tick */ });
    }, 30000);
    return () => { dead = true; clearInterval(t); };
  }, [bodyKey, bodyState]);

  const bodyText = useMemo(() => {
    if (!msg) return "";
    if (bodyState === "ready" && fullBody) return fullBody;
    return p.preview || "(no content preview)";
  }, [msg, p.preview, bodyState, fullBody]);

  // Airmail-parity archive shortcut (Jeff 2026-11-03): ⌘⌫ (Ctrl+Backspace
  // elsewhere) sets the open message aside — the same verb as the Set aside
  // button. Both ⌘ and Ctrl are accepted as the chord on every platform
  // (deterministic across browsers/hosts; Ctrl+Backspace has no conflicting
  // global browser binding, and typing targets are guarded below). Guarded:
  // never fires while typing in an input, textarea, or contenteditable, and
  // plain Backspace or Alt/Shift combos are left alone.
  useEffect(() => {
    if (!msg) return undefined;
    const onKey = async (e) => {
      if (e.key !== "Backspace" || e.altKey || e.shiftKey) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      e.preventDefault();
      try {
        const res = await board.act("archiveAny", msg.slug);
        if (res) onNotice(null);
        else onNotice("That action didn't go through — try again.");
      } catch (err) {
        onNotice(err?.message || "That action didn't go through — try again.");
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [msg, board, onNotice]);

  const sendReply = async () => {
    if (!replyText.trim() || replyState === "sending") return;
    setReplyState("sending"); setReplyMsg("");
    try {
      const res = await queueMailReply(msg.slug, replyText.trim());
      setReplyState("queued");
      setReplyMsg(res?.note || "Queued — Kit's engine sends it from the account it was written to.");
      setReplyText("");
    } catch (e) {
      setReplyState("error");
      setReplyMsg(e?.message || "Couldn't queue the reply.");
    }
  };

  if (!msg) {
    return (
      <div className="flex flex-1 items-center justify-center border-os-border bg-white p-10 text-center" data-testid="mail-reader-empty">
        <p className="max-w-[32ch] text-[0.875rem] leading-relaxed text-os-ink-3">
          Pick a message to read it here. Kit keeps this window to what needs you — the full archive stays in the engine.
        </p>
      </div>
    );
  }

  const act = async (verb, extra = {}) => {
    const res = await board.act(verb, msg.slug, extra.note);
    if (res) onNotice(null);
    else onNotice("That action didn't go through — try again.");
    return res;
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white" data-testid="mail-reader">
      {/* actions — flat client toolbar */}
      <div className="flex items-center gap-1.5 border-b border-[#D9D9DD] bg-[#FAFAFB] px-3 py-2">
        <button
          type="button"
          data-testid="mail-reply-toggle"
          onClick={() => setReplyOpen((v) => !v)}
          className="rounded-[5px] border border-os-border bg-white px-2.5 py-1 text-[11.5px] font-medium text-os-ink hover:bg-[#F2F2F4]"
>
          {replyOpen ? "Close reply" : "↩ Reply"}
        </button>
        <button
          type="button"
          data-testid="mail-set-aside"
          onClick={() => act("archiveAny")}
          className="rounded-[5px] border border-os-border bg-white px-2.5 py-1 text-[11.5px] font-medium text-os-ink hover:bg-[#F2F2F4]"
        >
          Set aside
        </button>
        <button
          type="button"
          data-testid="mail-note-toggle"
          onClick={() => setShowNote((v) => !v)}
          className="rounded-[5px] border border-os-border bg-white px-2.5 py-1 text-[11.5px] font-medium text-os-ink-2 hover:bg-[#F2F2F4]"
        >
          {showNote ? "Close note" : "Note"}
        </button>
        {p.mailbox === "answered" && (
          <MonoMeta as="span" className="ml-auto">handled — nothing owed</MonoMeta>
        )}
        {p.mailbox === "bounced" && (
          <MonoMeta as="span" className="ml-auto">couldn't deliver — keep or set aside</MonoMeta>
        )}
        {p.mailbox === "needs_reply" && (
          <MonoMeta as="span" className="ml-auto">
            waiting {p.ageDays} {p.ageDays === 1 ? "day" : "days"}
          </MonoMeta>
        )}
      </div>

      {/* note bar */}
      {showNote && (
        <div className="border-b border-os-border bg-os-wash px-4 py-2.5" data-testid="mail-note-bar">
          <div className="flex items-center gap-2">
            <input
              value={noteVal}
              onChange={(e) => setNoteVal(e.target.value)}
              placeholder="A note for the record — what this message is, what happens next"
              className="h-8 flex-1 rounded-control border border-os-border bg-white px-3 text-[12.5px] text-os-ink placeholder:text-os-ink-4"
            />
            <button
              type="button"
              onClick={async () => {
                if (!noteVal.trim()) return;
                const res = await act("note", { note: noteVal.trim() });
                if (res?.ok) { setNoteVal(""); setShowNote(false); }
              }}
              className="rounded-[5px] bg-os-ink px-3 py-1.5 text-[11.5px] font-semibold text-white"
            >
              Save note
            </button>
          </div>
        </div>
      )}

      {/* Kit's reply record — what went out, from where, when (Jeff 2026-10-20) */}
      {p.replyBody && (
        <div className="border-b border-os-border bg-[#F7F7F9] px-5 py-3" data-testid="mail-reply-record">
          <p className="font-mono text-[10px] uppercase tracking-[0.09em] text-os-ink-3">
            Kit's reply — sent {fmtStamp(p.sentAt)}{p.sentVia ? ` from ${p.sentVia}` : ` for ${BRAND_TITLE[p.brand] || "the desk"}`}
          </p>
          <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-os-ink-2">
            {p.replyBody}
          </p>
        </div>
      )}

      {/* head */}
      <div className="border-b border-os-border px-5 pb-3 pt-3.5">
        <h2 className="text-[17px] font-bold leading-snug tracking-[-0.01em] text-os-ink">{p.subject}</h2>
        <div className="mt-1.5 flex items-center gap-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[5px] bg-[#ECECEF] text-[10px] font-bold text-os-ink-2">
            {(p.from || "?").slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <div className="truncate text-[12.5px] font-semibold text-os-ink">
              {p.from} <span className="font-normal text-os-ink-3">· {p.brandLabel} · {p.channel}</span>
            </div>
            <div className="font-mono text-[10px] text-os-ink-3">{fmtStamp(p.sentAt)}</div>
          </div>
          {(p.flags || []).includes("money") && (
            <span className="ml-auto rounded bg-[rgba(194,43,16,0.08)] px-2 py-0.5 text-[10.5px] font-semibold text-os-red">money</span>
          )}
          {(p.flags || []).includes("waiting-old") && (
            <span className="ml-auto rounded bg-[rgba(194,43,16,0.08)] px-2 py-0.5 text-[10.5px] font-semibold text-os-red">
              waiting — old · {p.ageDays} days
            </span>
          )}
          {p.tier && (
            <span
              data-testid="mail-reader-tier"
              title={p.tierWhy || p.tier}
              className={cn(
                "ml-auto shrink-0 rounded px-2 py-0.5 text-[10.5px] font-semibold",
                p.tier === "human" && "bg-[rgba(46,125,50,0.09)] text-os-green",
                p.tier === "txn" && "bg-[#ECECEF] text-os-ink-2",
                p.tier === "noise" && "bg-[#ECECEF] text-os-ink-4",
              )}
            >
              {p.tier === "human" ? "human" : p.tier === "txn" ? "transaction" : "noise"}
            </span>
          )}
        </div>
      </div>

      {/* reply composer */}
      {replyOpen && (
        <div className="border-b border-os-border bg-[#FAFAFB] px-5 py-3" data-testid="mail-reply-composer">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            rows={5}
            placeholder="Write the reply — it goes out from the account this message was written to."
            className="min-h-[110px] w-full resize-y rounded-[8px] border border-os-border bg-white p-3 text-[13px] leading-relaxed text-os-ink placeholder:text-os-ink-4"
          />
          <div className="mt-2 flex items-center gap-2.5">
            <button
              type="button"
              data-testid="mail-reply-send"
              onClick={sendReply}
              disabled={!replyText.trim() || replyState === "sending"}
              className="rounded-[5px] bg-os-ink px-3.5 py-1.5 text-[11.5px] font-semibold text-white disabled:opacity-40"
            >
              {replyState === "sending" ? "Queueing…" : "Send reply"}
            </button>
            {replyMsg && (
              <span className={cn("text-[11.5px]", replyState === "error" ? "text-os-red" : "text-os-green")} data-testid="mail-reply-state">
                {replyMsg}
              </span>
            )}
          </div>
        </div>
      )}

      {/* body */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {bodyState === "missing" && (
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4" data-testid="mail-body-missing">
            full text loading — the preview is shown until the engine syncs (auto-retries, no re-click needed)
          </p>
        )}
        {fullBodyHtml ? (
          // HTML mail renders in a fully sandboxed iframe — sandbox="" is the
          // security boundary (no scripts, no same-origin, nothing). When the
          // message has no HTML leg the plain-text paragraph below still shows.
          <iframe
            title="Message content"
            srcDoc={fullBodyHtml}
            sandbox=""
            className="h-[560px] w-full border-0 bg-white"
            data-testid="mail-reader-html"
          />
        ) : (
          <p className="max-w-[70ch] whitespace-pre-wrap text-[13.5px] leading-[1.62] text-os-ink-2" data-testid="mail-reader-body">{bodyText}</p>
        )}
        {p.mailbox === "needs_reply" && (
          <div className="mt-5 max-w-[70ch] border-t border-dashed border-os-border-strong pt-4">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">
              Kit — reply draft
            </div>
            <div className="rounded-[8px] border border-os-border bg-white p-3.5 text-[13px] leading-relaxed text-os-ink-2">
              Not drafted yet — open this thread and I'll have a reply ready for your pass.
              <div className="mt-2 font-mono text-[10px] text-os-ink-4">
                phase 2 — drafted replies grounded in the thread, never invented
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── folder list (Sheet A) — one folder's messages, from the engine ─────────
function FolderList({ view, rows, busy, err }) {
  if (err) {
    return (
      <div className="p-6 text-center text-[12.5px] leading-relaxed text-os-amber" data-testid="mail-folder-error">
        {err}
      </div>
    );
  }
  if (busy && !rows) {
    return (
      <div className="p-6 text-center text-[12.5px] text-os-ink-3" data-testid="mail-folder-loading">
        Opening that folder…
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="p-6 text-center text-[12.5px] leading-relaxed text-os-ink-3" data-testid="mail-folder-empty">
        Nothing in {view.folder} right now.
      </div>
    );
  }
  return (
    <div data-testid="mail-folder-list">
      <p className="border-b border-[#E7E7EA] bg-[#FAFAFB] px-3 py-[3px] font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
        {view.folder} · {rows.length}
      </p>
      {rows.map((m) => (
        <div
          key={m.key}
          data-testid="mail-folder-row"
          className="grid grid-cols-[1fr_auto] gap-x-2 border-b border-[#E7E7EA] px-3 py-[7px]"
        >
          <span className="min-w-0">
            <span className="block truncate text-[12.5px] font-medium text-os-ink">{m.subject || "(no subject)"}</span>
            <span className="block truncate text-[11.5px] text-os-ink-3">{m.from || "(unknown sender)"}</span>
          </span>
          <span className="shrink-0 font-mono text-[10px] text-os-ink-4">{fmtWhen(m.sentAt)}</span>
        </div>
      ))}
    </div>
  );
}

// ── sent list (Sheet B) — what we sent, per account ────────────────────────
function SentList({ rows, busy, err }) {
  const [openKey, setOpenKey] = useState(null);
  const [body, setBody] = useState(null);
  const [bodyErr, setBodyErr] = useState("");

  useEffect(() => {
    if (!openKey) { setBody(null); setBodyErr(""); return; }
    let live = true;
    setBody(null);
    setBodyErr("");
    fetchMailSentOne(openKey)
      .then((d) => { if (live) setBody(d.sent || d); })
      .catch((e) => {
        if (!live) return;
        setBodyErr(e?.message || "Kit can't open that sent message right now.");
      });
    return () => { live = false; };
  }, [openKey]);

  if (err) {
    return (
      <div className="p-6 text-center text-[12.5px] leading-relaxed text-os-amber" data-testid="mail-sent-error">
        {err}
      </div>
    );
  }
  if (busy && !rows) {
    return (
      <div className="p-6 text-center text-[12.5px] text-os-ink-3" data-testid="mail-sent-loading">
        Opening the sent list…
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="p-6 text-center text-[12.5px] leading-relaxed text-os-ink-3" data-testid="mail-sent-empty">
        Nothing sent from this account yet.
      </div>
    );
  }
  return (
    <div data-testid="mail-sent-list">
      {rows.map((r) => {
        const open = openKey === r.key;
        const b = body && (body.key === r.key ? body : null);
        return (
          <div key={r.key} className="border-b border-[#E7E7EA]">
            <button
              type="button"
              data-testid="mail-sent-row"
              onClick={() => setOpenKey(open ? null : r.key)}
              className="block w-full px-3 py-[7px] text-left hover:bg-[#F2F2F4]"
            >
              <span className="flex items-baseline gap-1.5">
                <span className="truncate text-[12.5px] font-semibold text-os-ink">{r.subject || "(no subject)"}</span>
                <span className="ml-auto shrink-0 font-mono text-[10px] text-os-ink-4">{fmtWhen(r.sentAt)}</span>
              </span>
              <span className="mt-px block truncate text-[11.5px] text-os-ink-3">
                To: {r.to || "(nobody)"}
              </span>
              <span className="mt-px block truncate text-[11px] text-os-ink-4">
                From: {r.account || "(unknown account)"}
              </span>
            </button>
            {open && (
              <div className="px-3 pb-3 pt-1" data-testid="mail-sent-body">
                <div className="mb-1 text-[10.5px] font-semibold text-os-green">Sent by Kit</div>
                {bodyErr && <div className="text-[11.5px] text-os-amber">{bodyErr}</div>}
                {!bodyErr && !b && <div className="text-[11.5px] text-os-ink-4">Loading the message…</div>}
                {b && (
                  <div className="whitespace-pre-wrap rounded-[6px] border border-os-border bg-[#FAFAFB] p-2.5 text-[12px] leading-relaxed text-os-ink-2">
                    {b.body || "(no body stored)"}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── the client ───────────────────────────────────────────────
export function MailClient({ items, meta, feedItems, feedStamp, board, variant = "desktop", onNotice, brandScope }) {
  const desktop = useMediaQuery("(min-width: 1024px)") && variant === "desktop";
  const msgs = items.filter((i) => i.kind === MSG_KIND);
  const isScoped = !!brandScope;

  // Jeff's model (2026-10-20 evening): brand → account → to-process vs completed.
  const [brand, setBrand] = useState(isScoped ? brandScope.slug : "all");
  const [account, setAccount] = useState("all");
  const [status, setStatus] = useState("open"); // open = to process · done = completed
  const [tierFilter, setTierFilter] = useState("all"); // all | human | txn | noise (board UI buckets, Phase 3)
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchRes, setSearchRes] = useState(null); // null = not searching
  const [searchBusy, setSearchBusy] = useState(false);

  // ── folder browsing (Sheet A) ──────────────────────────────────────────
  // `folderView` is {mailbox, folder, role} — selecting a folder REPLACES the
  // board-bucket filter; it does not stack with it.
  const [folderView, setFolderView] = useState(null);
  const [folderRows, setFolderRows] = useState([]); // {mailbox: {folder: {...}}}
  const [folderMsgs, setFolderMsgs] = useState(null);
  const [folderBusy, setFolderBusy] = useState(false);
  const [folderErr, setFolderErr] = useState("");

  // ── sent (Sheet B) ─────────────────────────────────────────────────────
  const [sentView, setSentView] = useState(false);
  const [sentRows, setSentRows] = useState(null);
  const [sentBusy, setSentBusy] = useState(false);
  const [sentErr, setSentErr] = useState("");

  // The folder inventory comes from the engine (counts are the engine's, never
  // recomputed from a visible peek).
  useEffect(() => {
    let live = true;
    // Quiet errors on the 30s refresh leg: initial load surfaces errors, the
    // poll must never spam notices when one request hiccups.
    const loadFolders = (quiet = false) => {
      fetchMailFolders()
        .then((d) => {
          if (!live) return;
          const map = {};
          (d.accounts || []).forEach((acct) => {
            map[acct.mailbox] = { backing: acct.backing, sharedWith: acct.sharedWith || [] };
            (acct.folders || []).forEach((f) => {
              map[acct.mailbox][f.folder] = f;
            });
          });
          setFolderRows(map);
          if (!quiet) setFolderErr("");
        })
        .catch((e) => {
          if (!live) return;
          if (!quiet) setFolderErr(e?.message || "Kit can't reach the folder list right now.");
        });
    };
    loadFolders(false);
    const t = setInterval(() => {
      if (document.visibilityState === "visible") loadFolders(true);
    }, 30000);
    return () => { live = false; clearInterval(t); };
  }, []);

  // Selecting a folder loads that folder's messages.
  useEffect(() => {
    if (!folderView) { setFolderMsgs(null); setFolderErr(""); return; }
    let live = true;
    const loadMsgs = (quiet = false) => {
      if (!quiet) { setFolderBusy(true); setFolderErr(""); }
      fetchMailMessages(folderView.mailbox, folderView.folder, 50)
        .then((d) => { if (live) setFolderMsgs(d.messages || []); })
        .catch((e) => {
          if (!live || quiet) return;
          setFolderMsgs([]);
          setFolderErr(e?.message || "Kit can't reach this folder right now.");
          if (onNotice) onNotice(e?.message || "Kit can't reach this folder right now.");
        })
        .finally(() => { if (live && !quiet) setFolderBusy(false); });
    };
    loadMsgs(false);
    // Live arrival UI leg (spec Piece 3): a newly delivered message shows up in
    // the open folder within one poll cycle, no manual refresh.
    const t = setInterval(() => {
      if (document.visibilityState === "visible") loadMsgs(true);
    }, 30000);
    return () => { live = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderView?.mailbox, folderView?.folder]);

  // The Sent view: Gmail accounts have a server Sent folder, the Dovecot ones
  // have captured sends. One list, two sources (the backend merges them).
  // The account index is loaded once so the sidebar can show a Sent row for
  // every account that has one — without opening each one first.
  const [sentIndex, setSentIndex] = useState({});
  useEffect(() => {
    let live = true;
    fetchMailSent("", 500)
      .then((d) => {
        if (!live) return;
        const rows = d.sent || d.messages || [];
        const idx = {};
        rows.forEach((r) => {
          const acct = (r.account || "").toLowerCase();
          if (!acct) return;
          idx[acct] = (idx[acct] || 0) + 1;
        });
        setSentIndex(idx);
      })
      .catch(() => { /* the per-account list still surfaces the failure */ });
    return () => { live = false; };
  }, []);

  const sentAccountHas = (a) => !!sentIndex[a];

  useEffect(() => {
    if (!sentView) { setSentRows(null); setSentErr(""); return; }
    let live = true;
    setSentBusy(true);
    setSentErr("");
    fetchMailSent(account !== "all" && account !== "__legacy" ? account : "", 50)
      .then((d) => { if (live) setSentRows(d.sent || d.messages || []); })
      .catch((e) => {
        if (!live) return;
        setSentRows([]);
        setSentErr(e?.message || "Kit can't reach the sent list right now.");
        if (onNotice) onNotice(e?.message || "Kit can't reach the sent list right now.");
      })
      .finally(() => { if (live) setSentBusy(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentView, account]);

  // Airmail-style global search (subject · sender · stored full text)
  useEffect(() => {
    const q = searchQ.trim();
    if (!q) { setSearchRes(null); setSearchBusy(false); return; }
    setSearchBusy(true);
    const t = setTimeout(() => {
      searchMail(q)
        .then((d) => setSearchRes((d && d.results) || []))
        .catch(() => setSearchRes([]))
        .finally(() => setSearchBusy(false));
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ]);

  const matchesScope = (m) => {
    if (brand === "all") return true;
    return m.payload?.brand === brand;
  };
  const matchesAccount = (m) => {
    if (account === "all") return true;
    return account === "__legacy" ? !m.payload?.to : m.payload?.to === account;
  };
  const matchesStatus = (m) => {
    const mb = m.payload?.mailbox;
    if (status === "open") return mb === "needs_reply" || mb === "call_backs";
    return mb === "answered" || mb === "bounced";
  };

  // Triage tier — the mirror's classify pass stamps human | txn | noise on
  // every row (board UI buckets, Phase 3). `all` disables the filter.
  const tier = (m) => ((m.payload?.tier || "human") + "").toLowerCase();

  const visibleMsgs = useMemo(() => {
    const pool = msgs.filter((m) => m.state !== "ARCHIVED");
    return pool
      .filter((m) => matchesScope(m) && matchesAccount(m) && matchesStatus(m))
      .filter((m) => tierFilter === "all" || tier(m) === tierFilter)
      .sort((a, b) => new Date(b.payload?.sentAt || 0) - new Date(a.payload?.sentAt || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgs, brand, account, status, tierFilter]);

  const selected = useMemo(
    () => visibleMsgs.find((m) => m.slug === selectedSlug) || visibleMsgs[0] || null,
    [visibleMsgs, selectedSlug],
  );

  // live counts for the sidebar, computed from the full msg pool
  const openFor = (b_, acct) =>
    msgs.filter((m) => m.state !== "ARCHIVED" && (m.payload?.mailbox === "needs_reply" || m.payload?.mailbox === "call_backs")
      && (b_ === "all" || m.payload?.brand === b_)
      && (acct == null || (acct === "__legacy" ? !m.payload?.to : m.payload?.to === acct))).length;
  const doneFor = (b_) =>
    msgs.filter((m) => m.state !== "ARCHIVED" && (m.payload?.mailbox === "answered" || m.payload?.mailbox === "bounced")
      && (b_ === "all" || m.payload?.brand === b_)).length;
  // Triage bucket counts — open-side pool only (the classification is about
  // what's waiting, not what's answered). Rows without a tier stamp read human.
  const tierFor = (t) =>
    msgs.filter((m) => m.state !== "ARCHIVED" && (m.payload?.mailbox === "needs_reply" || m.payload?.mailbox === "call_backs")
      && tier(m) === t).length;

  // Airmail sidebar row: flat, dense, count chip right-aligned.
  const FolderRow = ({ on, onClick, dotTone, title, count, indent }) => (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-[26px] w-full items-center gap-1.5 rounded-[4px] text-left text-[12px]",
        indent ? "pl-5 pr-2" : "pl-2 pr-2",
        on ? "bg-[#DFDFE3] font-semibold text-os-ink" : "text-os-ink-2 hover:bg-black/[0.045]",
      )}
    >
      {dotTone && <span className={cn("h-[7px] w-[7px] shrink-0 rounded-full", dotTone === "red" ? "bg-os-red" : "bg-os-green")} />}
      <span className="truncate">{title}</span>
      {count != null && count > 0 && (
        <span className={cn(
          "ml-auto min-w-[18px] shrink-0 rounded-full px-[6px] text-center font-mono text-[9.5px] leading-[16px]",
          dotTone === "red" ? "bg-os-red text-white" : on ? "bg-os-ink text-white" : "bg-[#C9C9CE] text-os-ink",
        )}>
          {count}
        </span>
      )}
    </button>
  );

  const headerTitle = isScoped ? brandScope.name : "Mail";

  const SidebarContent = () => (
    <>
      <div className="px-2 pb-1 pt-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
        Mailboxes
      </div>
      <FolderRow
        on={brand === "all" && status === "open"}
        dotTone="red"
        title="To process"
        count={openFor("all", null)}
        onClick={() => { setBrand("all"); setAccount("all"); setStatus("open"); setSelectedSlug(null); }}
      />
      <FolderRow
        on={brand === "all" && status === "done"}
        dotTone="green"
        title="Completed"
        count={doneFor("all")}
        onClick={() => { setBrand("all"); setAccount("all"); setStatus("done"); setSelectedSlug(null); }}
      />

      {/* Triage buckets — Kit's classify pass (Phase 3): humans want replies,
          txn is receipts/notifications, noise is junk. Click filters the list. */}
      <div className="mx-2 my-2 h-px bg-[#D9D9DD]" />
      <div className="px-2 pb-1 font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
        Triage
      </div>
      <FolderRow
        on={tierFilter === "all"}
        title="All triage"
        count={tierFor("human") + tierFor("txn") + tierFor("noise")}
        onClick={() => { setTierFilter("all"); setSelectedSlug(null); }}
      />
      <FolderRow
        on={tierFilter === "human"}
        indent
        dotTone="red"
        title="Human — wants a reply"
        count={tierFor("human")}
        onClick={() => { setTierFilter("human"); setBrand("all"); setAccount("all"); setStatus("open"); setSelectedSlug(null); }}
      />
      <FolderRow
        on={tierFilter === "txn"}
        indent
        title="Transactions / receipts"
        count={tierFor("txn")}
        onClick={() => { setTierFilter("txn"); setBrand("all"); setAccount("all"); setStatus("open"); setSelectedSlug(null); }}
      />
      <FolderRow
        on={tierFilter === "noise"}
        indent
        title="Noise"
        count={tierFor("noise")}
        onClick={() => { setTierFilter("noise"); setBrand("all"); setAccount("all"); setStatus("open"); setSelectedSlug(null); }}
      />

      <div className="mx-2 my-2 h-px bg-[#D9D9DD]" />
      <div className="px-2 pb-1 font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
        Brands
      </div>
      {BRAND_LIST.map((b) => (
        <FolderRow
          key={b}
          on={brand === b && status === "open"}
          title={BRAND_TITLE[b] || b}
          count={openFor(b, null)}
          onClick={() => { setBrand(b); setAccount("all"); setStatus("open"); setSelectedSlug(null); }}
        />
      ))}

      {brand !== "all" && (
        <>
          <div className="mx-2 my-2 h-px bg-[#D9D9DD]" />
          <div className="px-2 pb-1 font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
            Accounts — {BRAND_TITLE[brand]}
          </div>
          <FolderRow
            on={account === "all" && status === "open" && !folderView && !sentView}
            title={`All ${BRAND_TITLE[brand]} accounts`}
            count={openFor(brand, null)}
            onClick={() => { setAccount("all"); setStatus("open"); setFolderView(null); setSentView(false); setSelectedSlug(null); }}
          />
          {(ACCOUNTS_BY_BRAND[brand] || []).map((a) => {
            const rec = ACCOUNT_RECORD[a] || {};
            const inv = folderRows[a];
            const folders = inv ? Object.values(inv).filter((f) => f && f.folder) : [];
            const isShared = !!(rec.backing && rec.backing !== a);
            return (
              <div key={a}>
                <FolderRow
                  on={account === a && !sentView}
                  indent
                  title={a}
                  count={openFor(brand, a)}
                  onClick={() => { setAccount(a); setFolderView(null); setSentView(false); setSelectedSlug(null); }}
                />
                {isShared && (
                  <div
                    className="flex items-center gap-1 pl-7 pr-2 py-[1px] text-[10px] text-os-ink-4"
                    data-testid={`mail-account-shared-${a}`}
                    title={`Shared store with ${rec.backing} — this account files no folders of its own`}
                  >
                    <span className="font-mono">ↂ</span>
                    <span className="truncate">shared with {rec.backing}</span>
                    {!rec.credentialed && (
                      <span className="ml-auto shrink-0 text-os-amber">no credential yet</span>
                    )}
                  </div>
                )}
                {!isShared && !rec.credentialed && (
                  <div
                    className="pl-7 pr-2 py-[1px] text-[10px] text-os-amber"
                    data-testid={`mail-account-nocred-${a}`}
                  >
                    no credential yet
                  </div>
                )}
                {/* Folders block — the engine's inventory. Absent folders are
                    hidden, not shown as "0" (an account with no Sent folder
                    simply has no Sent row). */}
                {folders.map((f) => {
                  const on = folderView?.mailbox === a && folderView?.folder === f.folder;
                  return (
                    <button
                      key={`${a}:${f.folder}`}
                      type="button"
                      data-testid={`mail-folder-${a}-${f.role}`}
                      onClick={() => {
                        setAccount(a);
                        setSentView(false);
                        setFolderView({ mailbox: a, folder: f.folder, role: f.role });
                        setSelectedSlug(null);
                      }}
                      className={cn(
                        "flex h-[24px] w-full items-center gap-1.5 rounded-[4px] pl-7 pr-2 text-left text-[11.5px]",
                        on ? "bg-[#DFDFE3] font-semibold text-os-ink" : "text-os-ink-2 hover:bg-black/[0.045]",
                      )}
                    >
                      <span className="truncate">{f.displayName || f.folder}</span>
                      {f.total != null && (
                        <span className="ml-auto shrink-0 font-mono text-[9.5px] text-os-ink-4">{f.total}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
          {/* Sent — one row per account that has one: a server Sent folder
              (role='sent') or captured sends. Hidden when neither exists. */}
          {(ACCOUNTS_BY_BRAND[brand] || []).map((a) => {
            const inv = folderRows[a];
            const hasSentFolder = inv && Object.values(inv).some((f) => f && f.role === "sent");
            const hasCaptured = sentAccountHas(a);
            if (!hasSentFolder && !hasCaptured) return null;
            const on = sentView && account === a;
            return (
              <button
                key={`sent:${a}`}
                type="button"
                data-testid={`mail-sent-row-${a}`}
                onClick={() => { setAccount(a); setFolderView(null); setSentView(true); setSelectedSlug(null); }}
                className={cn(
                  "flex h-[24px] w-full items-center gap-1.5 rounded-[4px] pl-7 pr-2 text-left text-[11.5px]",
                  on ? "bg-[#DFDFE3] font-semibold text-os-ink" : "text-os-ink-2 hover:bg-black/[0.045]",
                )}
              >
                <span className="truncate">Sent</span>
                <span className="ml-auto shrink-0 font-mono text-[9.5px] text-os-ink-4">{a}</span>
              </button>
            );
          })}
          <FolderRow
            on={account === "__legacy" && !folderView && !sentView}
            indent
            title="Older / unfiled"
            count={openFor(brand, "__legacy")}
            onClick={() => { setAccount("__legacy"); setFolderView(null); setSentView(false); setSelectedSlug(null); }}
          />
        </>
      )}

      <div className="mt-auto px-2 pb-1 pt-3 text-[10px] leading-relaxed text-os-ink-4">
        {meta?.source_stamp ? `synced ${fmtStamp(meta.source_stamp)}` : "waiting for Kit's first sync"}
      </div>
    </>
  );

  // Live account feeds — real mailbox state per account (IMAP peek; Google accounts incl.).
  // Newest messages expand inline under the clicked account row (Airmail mailbox behavior).
  const feedRows = Array.isArray(feedItems) ? feedItems.filter((i) => i.kind === "mail-feed-account") : [];
  const [openFeed, setOpenFeed] = useState(null);
  const feedStampText = feedStamp ? `live ${fmtStamp(feedStamp)}` : "";
  const FeedRow = ({ item }) => {
    const p = item.payload || {};
    const latest = p.latest || [];
    const open = openFeed === p.mailbox;
    const err = !!p.error;
    return (
      <>
        <button
          type="button"
          data-testid={`mail-feed-row-${p.mailbox}`}
          onClick={() => setOpenFeed(open ? null : p.mailbox)}
          className="flex w-full items-center gap-1.5 rounded-[4px] px-2 py-[3px] text-left text-[12px] hover:bg-black/[0.045]"
        >
          <span className={cn("h-[7px] w-[7px] shrink-0 rounded-full", err ? "bg-os-amber" : p.unseen > 0 ? "bg-os-red" : "bg-os-green")} />
          <span className="truncate">{p.mailbox}</span>
          {p.google && <span className="shrink-0 rounded-[3px] bg-[#E8E8EC] px-1 font-mono text-[8.5px] uppercase text-os-ink-3">g</span>}
          {p.backing && <span className="shrink-0 font-mono text-[8.5px] text-os-ink-4" title={`Shared store with ${p.backing} — counts are the shared box`}>ↂ</span>}
          {p.total != null && (
            <span className="ml-auto shrink-0 font-mono text-[9.5px] text-os-ink-4">{p.total}{p.unseen > 0 ? ` · ${p.unseen} new` : ""}</span>
          )}
        </button>
        {open && !err && (
          <div className="mb-1 ml-3 flex flex-col gap-px border-l-2 border-[#DFDFE3] pl-2 pt-1" data-testid={`mail-feed-pane-${p.mailbox}`}>
            {latest.length === 0 && <span className="py-0.5 text-[11px] text-os-ink-4">No recent mail in the peeked window.</span>}
            {latest.map((m, ix) => (
              <span key={ix} className={cn("flex items-baseline gap-1.5 py-[2px] text-[11px] leading-snug", m.seen ? "text-os-ink-3" : "font-semibold text-os-ink")}>
                <span className="w-[88px] shrink-0 truncate">{m.from}</span>
                <span className="truncate">{m.subject}</span>
              </span>
            ))}
            <span className="pt-0.5 font-mono text-[9px] text-os-ink-4">{feedStampText} · read-only peek — open your mail app to act</span>
          </div>
        )}
        {open && err && (
          <div className="mb-1 ml-3 border-l-2 border-[#DFDFE3] pl-2 pt-1 text-[11px] leading-snug text-os-amber" data-testid={`mail-feed-err-${p.mailbox}`}>
            This account didn't answer on the last check — Kit's next pass retries it.
          </div>
        )}
      </>
    );
  };
  const LiveFeeds = () => (
    <>
      <div className="mx-2 my-2 h-px bg-[#D9D9DD]" />
      <div className="px-2 pb-1 font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
        Live accounts
      </div>
      {feedRows.length === 0 && (
        <div className="px-2 pb-1 text-[11px] leading-snug text-os-ink-4">Account feeds arrive on Kit's next sync.</div>
      )}
      {feedRows.map((it) => <FeedRow key={it.slug} item={it} />)}
    </>
  );

  return (
    <div data-testid="mail-client" className="w-full text-os-ink">
      <div
        className={cn(
          "flex overflow-hidden border border-os-border bg-white",
          desktop ? "h-[calc(100vh-104px)] min-h-[540px] rounded-[10px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]" : "min-h-[520px] rounded-card",
        )}
      >
        {/* pane 1 — folders (Airmail sidebar) */}
        <aside className="hidden w-[218px] shrink-0 flex-col overflow-y-auto border-r border-[#D9D9DD] bg-[#F5F5F7] py-2 md:flex">
          <div className="mb-1.5 px-3 pt-1 text-[12.5px] font-bold text-os-ink">{headerTitle}</div>
          <SidebarContent />
          <LiveFeeds />
        </aside>

        {/* pane 2 — message list */}
        <section className={cn("flex min-h-0 w-full flex-col border-os-border bg-white md:w-[380px] md:shrink-0 md:border-r", !desktop && "hidden")} data-testid="mail-list">
          {/* toolbar — segmented control like Airmail's filter bar */}
          <div className="flex h-9 items-center gap-2 border-b border-[#D9D9DD] bg-[#FAFAFB] px-2.5">
            <div className="flex overflow-hidden rounded-[5px] border border-os-border" data-testid="mail-status-toggle">
              <button
                type="button"
                onClick={() => { setStatus("open"); setSelectedSlug(null); }}
                className={cn(
                  "px-2.5 py-1 text-[11px] font-semibold",
                  status === "open" ? "bg-os-ink text-white" : "bg-white text-os-ink-2 hover:bg-[#F2F2F4]",
                )}
              >
                To process
              </button>
              <button
                type="button"
                onClick={() => { setStatus("done"); setSelectedSlug(null); }}
                className={cn(
                  "border-l border-os-border px-2.5 py-1 text-[11px] font-semibold",
                  status === "done" ? "bg-os-ink text-white" : "bg-white text-os-ink-2 hover:bg-[#F2F2F4]",
                )}
              >
                Completed
              </button>
            </div>
            <input
            type="search"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search mail"
            data-testid="mail-search"
            className="ml-auto h-7 w-32 rounded-[5px] border border-os-border bg-white px-2.5 text-[11.5px] text-os-ink placeholder:text-os-ink-4 focus:w-44 focus:outline-none focus:ring-1 focus:ring-os-ink-3"
          />
            <span className="font-mono text-[10px] text-os-ink-4" data-testid="mail-shown-count">
              {visibleMsgs.length} shown
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto" data-testid="mail-msgs">
            {folderView ? (
              <FolderList
                view={folderView}
                rows={folderMsgs}
                busy={folderBusy}
                err={folderErr}
              />
            ) : sentView ? (
              <SentList rows={sentRows} busy={sentBusy} err={sentErr} />
            ) : (
            <>
            {visibleMsgs.length === 0 && (
              <div className="p-6 text-center text-[12.5px] leading-relaxed text-os-ink-3" data-testid="mail-list-empty">
                {status === "open"
                  ? "Nothing waiting here right now — the list refills on Kit's next sync."
                  : "Nothing completed in this view yet."}
              </div>
            )}
            {searchQ.trim() ? (
              <div>
                {(searchRes || []).length === 0 && (
                  <div className="p-6 text-center text-[12.5px] text-os-ink-3" data-testid="mail-search-empty">
                    {searchBusy ? "Searching…" : "No matches in stored mail yet — deeper mail syncs land on Kit’s passes."}
                  </div>
                )}
                {(searchRes || []).map((r) => (
                  <button key={r.key} type="button" onClick={() => setSearchQ("")}
                    className="block w-full border-b border-[#E7E7EA] px-3 py-2 text-left hover:bg-[#F2F2F4]">
                    <div className="truncate text-[12.5px] font-semibold text-os-ink">{r.subject || "(no subject)"}</div>
                    <div className="truncate text-[11.5px] text-os-ink-2">{r.from}</div>
                    <div className="truncate text-[11px] text-os-ink-3">{(r.head || "").slice(0, 90)}</div>
                  </button>
                ))}
              </div>
            ) : (
            (() => {
              // Airmail keeps date headers in the list — quiet gray hairlines
              const groups = [];
              let lastKey = null;
              const label = (m) => {
                const d = new Date(m.payload?.sentAt || "");
                if (Number.isNaN(d.getTime())) return null;
                const nowD = new Date();
                const one = new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate());
                const two = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                const diff = Math.round((one - two) / 86400000);
                if (diff <= 0) return "Today";
                if (diff === 1) return "Yesterday";
                return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
              };
              visibleMsgs.forEach((m) => {
                const k = label(m) || "Older";
                if (k !== lastKey) { groups.push({ key: k, rows: [] }); lastKey = k; }
                groups[groups.length - 1].rows.push(m);
              });
              return groups.map((g) => (
                <div key={g.key}>
                  <p data-testid="mail-day-break" className="border-y border-[#E7E7EA] bg-[#FAFAFB] px-3 py-[3px] font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-os-ink-4">
                    {g.key} · {g.rows.length}
                  </p>
                  {g.rows.map((m) => (
                    <MsgRow key={m.slug} msg={m} selected={selected?.slug === m.slug} onSelect={(x) => setSelectedSlug(x.slug)} />
                  ))}
                </div>
              ));
            })()
            )}
            </>
            )}
          </div>
        </section>

        {/* pane 3 — reader */}
        <Reader msg={selected} board={board} onNotice={onNotice} variant={variant} />
      </div>
    </div>
  );
}